import { useEffect, useRef, useState, type ComponentProps, type FC } from 'react';
import { cn } from '../lib/utils';
import { ScrollArea } from './scroll-area';

/** Within a pixel of the edge counts as at the edge (sub-pixel layout). */
const EDGE_TOLERANCE = 1;

/**
 * A `ScrollArea` whose top and bottom edges fade out while there is content
 * past them: nothing at the top until you scroll, nothing at the bottom
 * once you reach the end, and no fade at all when the content fits.
 *
 * The fade is a mask on the viewport, not a painted overlay, so it blends
 * into whatever sits behind the area. The root carries `data-fade-top` /
 * `data-fade-bottom` while each fade is showing, and the fade lengths are
 * registered properties (`--fade-scroll-area-top` / `-bottom` in
 * styles.css), so the fades ease in and out rather than snap.
 *
 * Children go inside the viewport as they would in `ScrollArea`. Radix wraps
 * them in a `display: table` element, which stops content shrinking to the
 * viewport; this component makes that wrapper a block.
 */
export const FadeScrollArea: FC<ComponentProps<typeof ScrollArea>> = ({
  className,
  children,
  ...props
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({ top: false, bottom: false });

  useEffect(() => {
    const viewport = ref.current?.querySelector<HTMLElement>(
      '[data-slot="scroll-area-viewport"]'
    );
    if (!viewport) return;

    const update = () => {
      const { scrollTop, scrollHeight, clientHeight } = viewport;
      const top = scrollTop > EDGE_TOLERANCE;
      const bottom = scrollTop + clientHeight < scrollHeight - EDGE_TOLERANCE;
      setEdges((current) =>
        current.top === top && current.bottom === bottom ? current : { top, bottom }
      );
    };

    update();
    viewport.addEventListener('scroll', update, { passive: true });
    // The area resizes with its container; its content grows as it loads.
    const observer = new ResizeObserver(update);
    observer.observe(viewport);
    if (viewport.firstElementChild) observer.observe(viewport.firstElementChild);

    return () => {
      viewport.removeEventListener('scroll', update);
      observer.disconnect();
    };
  }, []);

  return (
    <ScrollArea
      {...props}
      ref={ref}
      data-slot="fade-scroll-area"
      data-fade-top={edges.top || undefined}
      data-fade-bottom={edges.bottom || undefined}
      className={cn(
        '[&_[data-slot=scroll-area-viewport]>div]:block!',
        'transition-[--fade-scroll-area-top,--fade-scroll-area-bottom] duration-[var(--z-dur-fast)]',
        'data-fade-top:[--fade-scroll-area-top:2rem] data-fade-bottom:[--fade-scroll-area-bottom:3rem]',
        '[&_[data-slot=scroll-area-viewport]]:[mask-image:linear-gradient(to_bottom,transparent,black_var(--fade-scroll-area-top),black_calc(100%-var(--fade-scroll-area-bottom)),transparent)]',
        className
      )}
    >
      {children}
    </ScrollArea>
  );
};
